// src/shared/ui/components/form-action-bar.tsx
// The actions that commit or discard a form's edits (Save, Cancel, Discard, Update…) live with the content they
// act on, one layer below the page header, never in it (Antonin, 15 Oct: "this type of action belongs to the lower
// level content"). The bar is the last child of the scrolling page body and sticks to its bottom edge.
// `status` is the saved line on the left (the approved design's FormActionBar: "All changes saved" with a check,
// "Saving…", "Unsaved changes", or the error text); `note` is the quiet line after it (why Save is off); the buttons
// sit on the right. Look: showcase-bridge.css `/* form action bar */` + showcase-patient.css `/* form action bar status */`.
import type { ReactNode } from 'react';
import { Icon } from '@scripsteam/scrips-signal-ds';

export type FormSaveStatus = 'saved' | 'saving' | 'unsaved' | 'error';

interface FormActionBarProps {
  children: ReactNode;
  /** A quiet line left of the buttons, e.g. why Save is disabled. */
  note?: ReactNode;
  /** The saved status on the left: saved / saving / unsaved / error. Absent = no status line. */
  status?: FormSaveStatus;
  /** The words for `error` (why the save did not go through). Without it the error status is not drawn. */
  errorText?: string | null;
}

const STATUS_TEXT: Record<Exclude<FormSaveStatus, 'error'>, string> = {
  saved: 'All changes saved',
  saving: 'Saving…',
  unsaved: 'Unsaved changes',
};

export function FormActionBar({ children, note, status, errorText }: FormActionBarProps) {
  const text = status === 'error' ? (errorText ?? null) : status ? STATUS_TEXT[status] : null;
  return (
    <div data-slot="form-action-bar" className="form-action-bar">
      {text != null ? (
        <div
          data-slot="form-action-bar-status"
          data-status={status}
          role={status === 'error' ? 'alert' : 'status'}
          aria-live="polite"
        >
          {status === 'saved' ? <Icon name="check" size={16} color="currentColor" aria-hidden /> : null}
          <span>{text}</span>
        </div>
      ) : null}
      {note != null && <div className="form-action-bar-note">{note}</div>}
      <div data-slot="form-action-bar-actions">{children}</div>
    </div>
  );
}
